import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { ButtonLoadingSpinner } from './components/button-loading-spinner';
import {
	LoadingSpinnerColor,
	type LoadingSpinnerSize,
} from '../../atoms/loading-spinner/loading-spinner.types';
import { ButtonSize, ButtonVariant } from './button.type';
import { cn } from '@utils';

// Ensures adequate color contrast on spinner.
// NOTE: values are passed through ButtonSpinnerColorMap downstream, which inverts
// PRIMARY <-> WHITE. For transparent-background variants (hyperlink, icon, tableHeader)
// we pass WHITE so the final resolved color is the dark "primary" and stays visible.
const variantSpinnerColor: Record<ButtonVariant, LoadingSpinnerColor> = {
	[ButtonVariant.PRIMARY]: LoadingSpinnerColor.PRIMARY,
	[ButtonVariant.SECONDARY]: LoadingSpinnerColor.SECONDARY,
	[ButtonVariant.SECONDARY_BORDER]: LoadingSpinnerColor.SECONDARY,
	[ButtonVariant.HYPERLINK]: LoadingSpinnerColor.WHITE,
	[ButtonVariant.ICON]: LoadingSpinnerColor.WHITE,
	[ButtonVariant.TABLEHEADER]: LoadingSpinnerColor.WHITE,
	[ButtonVariant.PRIMARY_NEW]: LoadingSpinnerColor.PRIMARY,
	[ButtonVariant.SECONDARY_NEW]: LoadingSpinnerColor.WHITE,
	[ButtonVariant.TERTIARY_NEW]: LoadingSpinnerColor.SECONDARY,
};

const iconButtonSizeVariants = cva('', {
	variants: {
		size: {
			xs: 'size-2',
			sm: 'size-2.5',
			md: 'size-3',
			lg: 'size-3.5',
			xl: 'size-4',
			'2xl': 'size-5',
		},
	},
});

const buttonSizeVariants = cva('', {
	variants: {
		size: {
			xs: 'h-4 px-2 text-xs',
			sm: 'h-8 px-3 text-sm',
			md: 'h-9 px-3 text-base',
			lg: 'h-12 px-8 text-lg',
			xl: 'h-16 px-12 text-xl',
			'2xl': 'h-20 px-16 text-2xl',
		},
	},
});

const buttonVariants = cva(
	'transition-border focus-visible:ring-ring disabled:pointer-events-none relative inline-flex items-center justify-center whitespace-nowrap rounded-sm text-base font-bold transition-all focus-visible:outline-hidden focus-visible:ring-1 disabled:cursor-not-allowed disabled:text-textDisabled',
	{
		variants: {
			variant: {
				primary: 'bg-accent text-white hover:shadow-lg disabled:bg-bgPrimary',
				secondary: 'bg-none text-secondary hover:text-primary disabled:border-borderSeparator',
				hyperlink: 'p-0! font-medium text-secondary transition-all hover:underline',
				tableHeader: 'p-0! font-medium text-primary transition-all',
				secondaryBorder:
					'border border-secondary bg-none text-secondary hover:bg-white hover:shadow-sm disabled:border-borderSeparator',
				icon: 'aspect-square w-auto bg-transparent p-0 text-secondary hover:text-primary',
				// TODO: deprecate old variants and refactor accordingly https://bond-sports.atlassian.net/browse/BOND-14190
				primaryNew:
					'h-unset rounded-xl bg-primary px-2 py-1.5 font-semibold text-white hover:bg-accent disabled:bg-disabled',
				secondaryNew:
					'h-unset rounded-xl border border-primary bg-white px-2 py-1.5 font-semibold text-primary hover:border-accent disabled:border-disabled',
				tertiaryNew: 'h-unset border-none p-0! font-medium text-primary hover:text-textBlue',
			},
			isLoading: {
				true: 'pointer-events-none cursor-not-allowed',
			},
		},
		defaultVariants: {
			variant: 'primary',
		},
	}
);
export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants>,
		VariantProps<typeof buttonSizeVariants> {
	asChild?: boolean;
	isLoading?: boolean;
	innerContainerClassName?: string;
	iconButton?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			children,
			className,
			variant = ButtonVariant.PRIMARY,
			size = ButtonSize.MD,
			dangerouslySetInnerHTML,
			isLoading = false,
			asChild = false,
			onClick,
			innerContainerClassName,
			iconButton = false,
			...props
		},
		ref
	) => {
		const Comp = asChild ? (Slot as React.ElementType) : 'button';

		const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			if (isLoading) {
				event.preventDefault();
				event.stopPropagation();
			} else {
				onClick?.(event);
			}
		};

		const sizeClass = iconButton ? iconButtonSizeVariants({ size }) : buttonSizeVariants({ size });
		const spinnerColor =
			variantSpinnerColor[variant as ButtonVariant] ?? LoadingSpinnerColor.PRIMARY;

		return (
			<Comp
				className={cn(buttonVariants({ variant, isLoading }), sizeClass, className)}
				dangerouslySetInnerHTML={dangerouslySetInnerHTML as { __html: string } | undefined}
				ref={ref}
				onClick={handleClick}
				data-aid="button"
				data-testid="button"
				{...props}
			>
				{asChild ? (
					children
				) : (
					<>
						<ButtonLoadingSpinner
							size={size as LoadingSpinnerSize}
							color={spinnerColor}
							className={`${isLoading ? 'absolute transition duration-300' : 'hidden'}`}
						/>
						<div
							className={cn(
								`${isLoading ? 'opacity-0' : ''} flex items-center transition duration-300`,
								innerContainerClassName
							)}
						>
							{children}
						</div>
					</>
				)}
			</Comp>
		);
	}
);
Button.displayName = 'Button';

export { Button, buttonVariants };
